import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BlogWishlist = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedWishlistId, setSelectedWishlistId] = useState(null);

  // useEffect to call fetchWishlistData
  useEffect(() => {
    fetchWishlistData();
  }, []);

  // fetch tool wishlist data
  const fetchWishlistData = async () => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = userRes.data;

      const wishlistRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/blog-wishlists?filters[email][$eq]=${userData.email}&populate=blog`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const wishlistItems = wishlistRes.data.data.map((item) => {
        const wishlistData = { id: item.id, ...(item.attributes || item) };
        const blogData = wishlistData.blog?.data ? { id: wishlistData.blog.data.id, ...(wishlistData.blog.data.attributes || wishlistData.blog.data) } : (wishlistData.blog || {});
        return {
          wishlistId: wishlistData.id,
          ...blogData,
          blogId: blogData.id,
        };
      });

      setWishlistItems(wishlistItems);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load wishlist items");
    } finally {
      setLoading(false);
    }
  };

  // handle remove item
  const handleRemoveFromWishlist = async () => {
    const token = localStorage.getItem("jwt");
    if (!token || !selectedWishlistId) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/blog-wishlists/${selectedWishlistId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchWishlistData();
      setShowConfirmationDialog(false);
      setSelectedWishlistId(null);

      toast.success("Removed from the wishlist!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error removing from wishlist:", err);

      toast.error("Failed to remove item from wishlist!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // cancel removal
  const handleCancelRemoval = () => {
    setShowConfirmationDialog(false);
    setSelectedWishlistId(null);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center text-white">
        {error}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col text-white">
      {/* Header */}
      <header className="bg-gray-900 p-4 text-center text-2xl">My Blogs</header>

      {/* Wishlist Content */}
      <div className="p-4 flex flex-col items-center overflow-y-auto mt-10">
        {wishlistItems.length === 0 ? (
          <div className="text-center text-gray-400">
            Your blog wishlist is empty
          </div>
        ) : (
          wishlistItems.map((item) => (
            <Link
              key={item.wishlistId}
              to="/blog"
              className="bg-gray-800 w-full sm:w-[90%] md:w-[600px] rounded-lg shadow-md p-4 mb-4 flex flex-col sm:flex-row items-center"
            >
              <img
                src={item.image || "https://via.placeholder.com/150"}
                alt={item.name}
                className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md mb-4 sm:mb-0 sm:mr-4"
              />

              <div className="flex-grow">
                <h2 className="text-lg font-semibold">{item.title}</h2>

                <h3 className="text-yellow-500">
                  <span className="text-white">by</span> {item.author}
                </h3>
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedWishlistId(item.wishlistId);
                  setShowConfirmationDialog(true);
                }}
                className="bg-red-600 text-white px-4 py-2 mt-4 sm:mt-0 sm:ml-4 rounded-lg hover:bg-red-700 transition"
              >
                Remove
              </button>
            </Link>
          ))
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-black">
              Are you sure you want to remove this item from your wishlist?
            </h3>
            <div className="flex justify-between">
              <button
                onClick={handleCancelRemoval}
                className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveFromWishlist}
                className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default BlogWishlist;
